import AddIcon from "@mui/icons-material/Add";
import type { ButtonProps } from "@mui/material/Button";
import { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";

import type { IProject } from "@citrine/resources/project";
import { ProjectClient } from "@citrine/resources/project";
import type { Team } from "@citrine/resources/team";
import { wrapFormikErrors } from "@citrine/utils/validation";

import { CancelButton, DialogButton, SubmitButton } from "#src/components/form";
import { useFindAllTeamsWithActions } from "#src/hooks/use-find-all-teams-with-actions";
import { useResourceNotifications } from "#src/hooks/use-resource-notifications";
import { useValidation } from "#src/hooks/use-validation";

import { NewProjectDialogContent, createEmptyProject } from "./common";

export interface ProjectValues extends Omit<IProject, "team"> {
  teamId: string;
}

interface NewProjectDialogProps {
  color?: ButtonProps["color"];
  onCreate: (project: IProject) => void;
}

function NewProjectDialogImpl({
  color,
  onCreate,
  teams,
}: NewProjectDialogProps & { teams: Team[] }) {
  const [errorMessage, setErrorMessage] = useState<string>();

  const [teamId, setTeamId] = useState("");

  const projectCreateNotifications = useResourceNotifications(
    "project",
    "create"
  );
  const { mutateAsync: createProject } = ProjectClient.getInstance(
    teamId
  ).useCreate({
    ...projectCreateNotifications,
    onSuccess(newProject) {
      setErrorMessage(undefined);
      projectCreateNotifications.onSuccess();
      onCreate(newProject);
    },
  });

  const validator = useValidation();
  const validate = useCallback(
    (project: ProjectValues) =>
      wrapFormikErrors({
        name: validator.isRequired(project.name),
        teamId: validator.isRequired(project.teamId),
      }),
    [validator]
  );

  return (
    <DialogButton<ProjectValues>
      data-testid="new-project-button"
      color={color}
      size="large"
      label={<FormattedMessage id="resources.project.create" />}
      startIcon={<AddIcon />}
      title={<FormattedMessage id="resources.project.create" />}
      // clearing the error message on close makes it flash as the dialog fades out
      // instead, clear it immediately before open
      getInitialValues={() => {
        setErrorMessage(undefined);
        return { ...createEmptyProject(), teamId: "" };
      }}
      validate={validate}
      validateOnBlur={false}
      onSubmit={async (project) => {
        try {
          await createProject(project);
        } catch (e) {
          setErrorMessage(
            e.response?.data?.errors?.[0]?.msg ??
              e.response?.data?.message ??
              e.message
          );
          // keep the dialog open
          return false;
        }
      }}
      actions={
        <>
          <CancelButton />
          <SubmitButton data-fullstoryid="projects.dialog.submit">
            <FormattedMessage id="projects.dialog.submit" />
          </SubmitButton>
        </>
      }
    >
      <NewProjectDialogContent
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        teams={teams}
        setTeamId={setTeamId}
      />
    </DialogButton>
  );
}

export function NewProjectDialog(props: NewProjectDialogProps) {
  const membershipsQuery = useFindAllTeamsWithActions();

  const writableTeams = membershipsQuery.data?.filter((membership) =>
    membership.actions.includes("WRITE")
  );

  return writableTeams?.length ? (
    <NewProjectDialogImpl {...props} teams={writableTeams} />
  ) : null;
}
